import { useEffect, useState } from 'react'
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { StoredImage } from '../../hooks/useImageStore'
import { ImagePromptResult } from '../../util/imagePromptTesting'
import Button from '../Button'
import SimpleLoader from '../SimpleLoader'

interface ImageCardGridProps {
    images: StoredImage[]
    onDelete: (id: string) => void
    onTargetChange: (id: string, target: 'Yes' | 'No' | null) => void
    results: Map<string, ImagePromptResult>
    moderationBoundary: number
    onRerunSingle: (fileName: string) => Promise<void>
    rerunningFileName: string | null
    resultsStale: boolean
}

function getResultLabel(result: ImagePromptResult, moderationBoundary: number) {
    const positive = result.score !== null ? result.score >= moderationBoundary : null
    if (positive === null) return { text: 'ERROR', color: 'text-orange-600 bg-orange-50 border-orange-200' }
    if (result.target === 'Yes' && positive) return { text: 'SUCCESS', color: 'text-green-700 bg-green-50 border-green-200' }
    if (result.target === 'No' && !positive) return { text: 'SUCCESS', color: 'text-green-700 bg-green-50 border-green-200' }
    if (result.target === 'Yes' && !positive) return { text: 'FAIL — false negative', color: 'text-red-700 bg-red-50 border-red-200' }
    if (result.target === 'No' && positive) return { text: 'FAIL — false positive', color: 'text-red-700 bg-red-50 border-red-200' }
    return { text: '', color: '' }
}

function ImageCard({
    image,
    onDelete,
    onTargetChange,
    result,
    moderationBoundary,
    onRerun,
    isRerunning,
    resultsStale,
}: {
    image: StoredImage
    onDelete: () => void
    onTargetChange: (target: 'Yes' | 'No' | null) => void
    result: ImagePromptResult | undefined
    moderationBoundary: number
    onRerun: () => void
    isRerunning: boolean
    resultsStale: boolean
}) {
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)

    useEffect(() => {
        const url = URL.createObjectURL(image.blob)
        setThumbnailUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [image.blob])

    const handleToggle = (value: 'Yes' | 'No') => {
        onTargetChange(image.target === value ? null : value)
    }

    const hasResult = result !== undefined
    const label = hasResult ? getResultLabel(result, moderationBoundary) : null
    const actual = hasResult && result.score !== null ? result.score >= moderationBoundary : null

    return (
        <div className={clsx(
            'border rounded-lg overflow-hidden bg-white w-52 flex flex-col transition-opacity',
            hasResult && !resultsStale
                ? label?.text.startsWith('FAIL') ? 'border-red-300' : label?.text === 'SUCCESS' ? 'border-green-300' : 'border-gray-200'
                : 'border-gray-200',
            hasResult && resultsStale && 'opacity-60'
        )}>
            {/* Image */}
            <div className="h-36 bg-gray-100 relative group flex-shrink-0">
                {thumbnailUrl && (
                    <>
                        <img
                            src={thumbnailUrl}
                            alt={image.name}
                            className="h-full w-full object-cover"
                        />
                        <a
                            href={thumbnailUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors"
                        >
                            <ArrowTopRightOnSquareIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                        </a>
                    </>
                )}
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete() }}
                    className="absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-black/70 z-10"
                >
                    <XMarkIcon className="w-3 h-3" />
                </button>
            </div>

            <div className="p-2 flex flex-col flex-1">
                {/* Filename */}
                <div className="text-xs text-gray-700 mb-1.5 truncate" title={image.name}>
                    {image.name}
                </div>

                {/* Yes/No toggle */}
                <div className="flex gap-1">
                    <button
                        onClick={() => handleToggle('Yes')}
                        className={clsx(
                            'flex-1 py-1 rounded text-xs font-medium transition-colors',
                            image.target === 'Yes'
                                ? 'border-2 border-red-600 bg-red-50 text-red-600'
                                : 'border border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        )}
                    >
                        Yes
                    </button>
                    <button
                        onClick={() => handleToggle('No')}
                        className={clsx(
                            'flex-1 py-1 rounded text-xs font-medium transition-colors',
                            image.target === 'No'
                                ? 'border-2 border-green-600 bg-green-50 text-green-600'
                                : 'border border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        )}
                    >
                        No
                    </button>
                </div>

                {/* Result section — always visible, placeholder when no result */}
                <div className="mt-2 pt-2 border-t border-gray-100 flex-1 flex flex-col">
                    {hasResult ? (
                        <>
                            {/* Result badge */}
                            <div
                                className={clsx(
                                    'inline-block px-2 py-0.5 rounded border text-xs font-semibold self-start',
                                    label!.color
                                )}
                            >
                                {label!.text}
                            </div>

                            {/* Actual + score */}
                            <div className="flex items-center gap-2 mt-1.5 text-xs">
                                <span className="text-gray-600">Actual:</span>
                                <span className="font-medium text-gray-900">
                                    {actual === null ? '—' : actual ? 'Yes' : 'No'}
                                </span>
                                {result.score !== null && (
                                    <span className="text-gray-600 ml-auto" title={`Raw score: ${result.score.toFixed(4)}`}>
                                        {result.score.toFixed(2)}
                                    </span>
                                )}
                            </div>

                            {/* Justification */}
                            <div className="text-xs text-gray-600 leading-relaxed mt-1.5 flex-1">
                                {result.justification}
                            </div>

                            {/* Re-run */}
                            <div className="flex justify-end mt-2">
                                {isRerunning ? (
                                    <SimpleLoader loading={true} />
                                ) : (
                                    <Button text="Re-run" onClick={onRerun} primary={false} />
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <span className="text-xs text-gray-600 italic">No results yet</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default function ImageCardGrid({
    images,
    onDelete,
    onTargetChange,
    results,
    moderationBoundary,
    onRerunSingle,
    rerunningFileName,
    resultsStale,
}: ImageCardGridProps) {
    return (
        <div className="flex flex-wrap gap-3 mt-3">
            {images.map((image) => (
                <ImageCard
                    key={image.id}
                    image={image}
                    onDelete={() => onDelete(image.id)}
                    onTargetChange={(target) => onTargetChange(image.id, target)}
                    result={results.get(image.name)}
                    moderationBoundary={moderationBoundary}
                    onRerun={() => onRerunSingle(image.name)}
                    isRerunning={rerunningFileName === image.name}
                    resultsStale={resultsStale}
                />
            ))}
        </div>
    )
}
